import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

import { CardView } from './cardview-grid.types';
import { InfoBarItemComponent } from './info-bar-item.component';

import { WidgetTypeEntity } from '~/repositories/widgets/store/widgets.models';
import { FileService } from '~/shared/services';

@Component({
  selector: 'app-cardview-grid',
  templateUrl: './cardview-grid.component.html',
  styleUrls: ['./cardview-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CardviewGridComponent implements AfterViewInit, OnDestroy {
  private readonly fileService = inject(FileService);

  @Input() config: CardView;
  @Input() widgetType: WidgetTypeEntity;
  @Input() checkedItems: CardView[];
  @Input() isHalfWidth: boolean;

  @Input() set detailPath(value: string) {
    this._detailPath = `${value}`;
  }

  @Input() set downloadPath(value: string) {
    this._downloadPath = `${value}`;
  }

  @Output() toggleSelected: EventEmitter<boolean> = new EventEmitter();
  @Output() taskClicked: EventEmitter<void> = new EventEmitter();
  @Output() handleRemoveItem: EventEmitter<string> = new EventEmitter();
  @Output() handleEditItem: EventEmitter<string> = new EventEmitter();
  @Output() selectVerticalEllipsisItem: EventEmitter<string> = new EventEmitter();

  @ViewChild('infoItems') infoItems: ElementRef<HTMLElement>;
  @ViewChildren(InfoBarItemComponent)
  infobarContainerItems!: QueryList<InfoBarItemComponent>;

  showMoreMeta = false;
  showDocumentsList = false;
  selected: string[] = [];
  isTaskItem = false;
  _detailPath: string;
  _downloadPath: string;

  overflowedItems = signal<InfoBarItemComponent[]>([]);

  private readonly destroyed$: Subject<boolean> = new Subject<boolean>();

  get hasToShowMainInfoComponent() {
    const { hd, yearNumber, title } = this.config;
    return this.hasToShowMainInfoLabelsComponent || hd || yearNumber || title;
  }

  get hasToShowMainInfoLabelsComponent() {
    const { new: isNew, needsAttention, bundleId } = this.config;
    return isNew || needsAttention || bundleId > 0;
  }

  get hasToShowTaskComponent() {
    const { task, assigned } = this.config;
    return task || assigned;
  }

  get hasToShowStatusComponent() {
    const { status } = this.config;
    return status?.length > 0;
  }

  get hasToShowActionsComponent() {
    const { userCanDeleteItem, userCanEditItem, showVerticalEllipsis } = this.config;
    return userCanDeleteItem || userCanEditItem || showVerticalEllipsis;
  }

  get hasMetaData() {
    const { documents, labels, info, timeline, optionalInfo, openTasks } = this.config;
    return documents?.length || labels?.length || info?.length || timeline || optionalInfo || openTasks;
  }

  get assignedTooltip() {
    const tooltips = this.config?.task?.tooltips;
    if (tooltips == null || tooltips.length === 0 || tooltips?.length === 1) {
      return null;
    }
    return this.config.task.tooltips.reduce((tooltip, nextLine) => (tooltip += '<p>' + nextLine + '</p>'), '');
  }

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private messageService: MessageService
  ) {}

  ngAfterViewInit(): void {
    this.handleOverflow();

    fromEvent(window, 'resize')
      .pipe(debounceTime(200))
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.handleOverflow());

    this.isTaskItem = this.widgetType?.code.endsWith('_TASKS');
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // Calculate if item is in overflow area
  // Emit value & index to handle the visiblity in parent component
  handleOverflow() {
    const container = this.infoItems?.nativeElement;
    if (!container) {
      return;
    }
    const items = Array.from(container.children);
    const newHiddenItems: InfoBarItemComponent[] = [];

    const { bottom: containerBottom } = container.getBoundingClientRect();
    this.showMoreMeta = false;

    items?.forEach((item, index) => {
      const { top: itemTop } = item.getBoundingClientRect();
      if (containerBottom <= itemTop) {
        newHiddenItems.push(this.infobarContainerItems.get(index));
      }
    });

    this.overflowedItems.set(newHiddenItems);
  }

  handleShowMore(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.showMoreMeta = !this.showMoreMeta;
  }

  toggleDocumentsList(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.showDocumentsList = !this.showDocumentsList;
  }

  selectItem(event: CheckboxChangeEvent) {
    this.toggleSelected.emit(event.checked);
  }

  clickTask() {
    this.taskClicked.emit();
  }

  navigateToDetail() {
    this.router.navigateByUrl(this._detailPath);
  }

  handleRemoveListItem(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.handleRemoveItem.emit(this.config.id);
  }

  handleEditListItem() {
    this.handleEditItem.emit(this.config.id);
  }

  downloadFile({ name, downloadPath }: { name: string; downloadPath: string }) {
    try {
      this.fileService.downloadFile(`${environment.BACKEND_URL}${downloadPath}`, name);
    } catch (err) {
      console.error(err);
      this.messageService.add({
        severity: 'error',
        detail: this.translateService.instant('error.document.download'),
        life: 5000
      });
    }
  }

  handleSelectVerticalEllipsisItem() {
    this.selectVerticalEllipsisItem.emit(this.config.id);
  }
}
